import { Injectable, computed, signal } from '@angular/core';

import { AdminActionMenuContext, AdminActionMenuRegistration } from '../models/admin-action-menu.model';
import { UserRolesService } from './user-roles.service';

interface RegisteredAdminActionMenuContext {
  registrationId: string;
  order: number;
  context: AdminActionMenuContext;
}

@Injectable({ providedIn: 'root' })
export class AdminActionMenuService {
  private readonly contextsSignal = signal<RegisteredAdminActionMenuContext[]>([]);
  private readonly openSignal = signal(false);
  private nextRegistrationId = 0;
  private nextOrder = 0;

  constructor(private userRolesService: UserRolesService) {}

  readonly isOpen = computed(() => this.openSignal());
  readonly currentContext = computed(() => {
    const contexts = this.contextsSignal();

    if (!contexts.length) {
      return null;
    }

    return [...contexts]
      .sort((a, b) => {
        const priorityDelta = this.getPriority(b.context) - this.getPriority(a.context);
        if (priorityDelta !== 0) {
          return priorityDelta;
        }

        return b.order - a.order;
      })[0]?.context ?? null;
  });
  readonly visibleSections = computed(() => {
    if (!this.userRolesService.isSystemAdmin()) {
      return [];
    }

    const context = this.currentContext();
    return (context?.sections ?? []).filter((section) => section.actions.length > 0);
  });
  readonly hasVisibleActions = computed(() => this.visibleSections().length > 0);
  readonly currentActionCount = computed(() =>
    this.visibleSections().reduce((count, section) => count + section.actions.length, 0)
  );

  registerContext(context: AdminActionMenuContext): AdminActionMenuRegistration {
    const registrationId = `admin-context-${this.nextRegistrationId++}`;
    const initialOrder = ++this.nextOrder;

    this.contextsSignal.update((contexts) => [
      ...contexts,
      {
        registrationId,
        order: initialOrder,
        context,
      }
    ]);

    return {
      update: (nextContext: AdminActionMenuContext) => {
        const nextOrder = ++this.nextOrder;
        this.contextsSignal.update((contexts) => contexts.map((entry) =>
          entry.registrationId === registrationId
            ? { ...entry, order: nextOrder, context: nextContext }
            : entry
        ));
      },
      dispose: () => {
        this.contextsSignal.update((contexts) => contexts.filter((entry) => entry.registrationId !== registrationId));

        if (this.contextsSignal().length === 0) {
          this.close();
        }
      }
    };
  }

  open(): void {
    if (!this.userRolesService.isSystemAdmin() || !this.hasVisibleActions()) {
      return;
    }

    this.openSignal.set(true);
  }

  close(): void {
    this.openSignal.set(false);
  }

  toggle(): void {
    if (!this.userRolesService.isSystemAdmin() || !this.hasVisibleActions()) {
      this.close();
      return;
    }

    this.openSignal.update((isOpen) => !isOpen);
  }

  private getPriority(context: AdminActionMenuContext): number {
    if (context.priority !== undefined) {
      return context.priority;
    }

    return context.kind === 'modal' ? 200 : 100;
  }
}
